import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookUser, Loader2, MapPin, Phone, Mail, Search } from "lucide-react";
import { toast } from "sonner";

export default function Annuaire() {
  const { user } = useAuth();
  const isAdmin = user && ["owner", "admin"].includes(user.role);
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [q, setQ] = useState("");
  const [cfg, setCfg] = useState({ directory_opt_in:false, directory_city:"", directory_description:"", directory_services:"" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/directory")];
      if (isAdmin) reqs.push(api.get("/settings"));
      const res = await Promise.all(reqs);
      setRows(res[0].data);
      if (isAdmin && res[1]) { const s = res[1].data; setCfg({ directory_opt_in: !!s.directory_opt_in, directory_city: s.directory_city || "", directory_description: s.directory_description || "", directory_services: s.directory_services || "" }); }
    } finally { setLoading(false); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    try { await api.put("/settings", cfg); toast.success("Fiche annuaire enregistrée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const filtered = rows.filter((r) => !q || `${r.name} ${r.city} ${r.services}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Annuaire des pros" subtitle="Le réseau des professionnels du detailing Auto Detail Manager" icon={BookUser} />

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 space-y-3" data-testid="dir-settings">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800"><input type="checkbox" data-testid="dir-optin" checked={cfg.directory_opt_in} onChange={(e)=>setCfg({...cfg,directory_opt_in:e.target.checked})} />Apparaître dans l'annuaire public</label>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Ville</Label><Input data-testid="dir-city" value={cfg.directory_city} onChange={(e)=>setCfg({...cfg,directory_city:e.target.value})} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Prestations phares</Label><Input data-testid="dir-services" value={cfg.directory_services} onChange={(e)=>setCfg({...cfg,directory_services:e.target.value})} className="bg-slate-50 border-slate-200" placeholder="Lavage, polissage, céramique…" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Présentation</Label><textarea data-testid="dir-desc" rows={2} value={cfg.directory_description} onChange={(e)=>setCfg({...cfg,directory_description:e.target.value})} className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" /></div>
          <Button data-testid="dir-save" onClick={saveCfg} variant="outline" className="border-slate-200">Enregistrer ma fiche</Button>
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="dir-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Rechercher une entreprise, une ville…" className="pl-9 bg-white border-slate-200" />
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> :
        filtered.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="dir-empty">Aucun professionnel inscrit dans l'annuaire pour le moment. Activez votre fiche pour être le premier !</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dir-list">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`dir-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="font-heading font-semibold text-slate-900">{r.name}</div>
              {r.city && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{r.city}</div>}
              {r.services && <div className="text-xs text-emerald-600 mt-2">{r.services}</div>}
              {r.description && <div className="text-xs text-slate-500 mt-2 line-clamp-3">{r.description}</div>}
              <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>}
                {r.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{r.email}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
